import { FiniteCarouselBase } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/FiniteCarouselBase";
import { BaseCarouselProps } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/CarouselBase";
import { Context } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { TrackedLink } from "@faire/deprecated-retailer-visitor-shared/components/TrackedLink/index";
import { EAEAEA_PIXEL } from "@faire/deprecated-retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { PAGE_TYPE } from "@faire/deprecated-retailer-visitor-shared/consts/PAGE_TYPE";
import { localizedUrlWithQueryParams } from "@faire/deprecated-retailer-visitor-shared/lib/pathUtils";
import { Core } from "@faire/slate/foundation/color";
import { isNotUndefined } from "@faire/typescript";
import { ILayoutElementData } from "@faire/web-api--source/indigofair/data/ILayoutElementData";
import { ISearchQueryTile } from "@faire/web-api--source/indigofair/data/ISearchQueryTile";
import { ISearchRequestReferrer } from "@faire/web-api--source/indigofair/data/ISearchRequestReferrer";
import { optimizeImageUrlFillSquare } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { TransparencyOverlay } from "@faire/web/ui/TransparencyOverlay";
import React from "react";
import styled, { css } from "styled-components";

const IMAGE_DIMENSION = 88;
export const SearchTiles: React.FC<BaseCarouselProps> = () => {
  const { layoutElement, onItemClick, requestId, pageType } =
    React.useContext(Context);
  const isSSR = useIsSSR();

  const renderItem = (item: ISearchQueryTile, index: number) => {
    const isHero = !!layoutElement.background_color;
    const imageUrl = item.image
      ? optimizeImageUrlFillSquare(item.image, isSSR, IMAGE_DIMENSION, {
          ignoreResizeTag: true,
        })
      : EAEAEA_PIXEL;
    const tileLink = item.cta_link ?? "/";
    const link =
      pageType === PAGE_TYPE.HOMEPAGE
        ? localizedUrlWithQueryParams(tileLink, {
            refType:
              ISearchRequestReferrer.SearchRequestReferrerType
                .HOMEPAGE_RECOMMENDED_SEARCHES,
            refReqId: requestId,
          })
        : tileLink;

    return (
      <SearchTile
        key={index}
        to={link}
        token={item.query ?? ""}
        onClick={() => {
          if (layoutElement.on_click_event_name) {
            onItemClick?.(layoutElement.on_click_event_name, requestId, "");
          }
        }}
      >
        <ImageContainer $withShadow={isHero}>
          <TransparencyOverlay />
          <img src={imageUrl} alt={item.query} />
        </ImageContainer>
        <Spacer height="1x" />
        <Typography color={layoutElement.main_text_color} align="center">
          {item.query}
        </Typography>
      </SearchTile>
    );
  };

  const getItems = () => {
    return layoutElement.layout_element_data
      .map((elementData: ILayoutElementData) => elementData.search_query_tile)
      .filter(isNotUndefined);
  };

  return <FiniteCarouselBase renderItem={renderItem} items={getItems()} />;
};

const SearchTile = styled(TrackedLink)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: ${getSpacing("2x")};

  ${responsiveMediaQueries.desktopAndAbove} {
    width: ${IMAGE_DIMENSION}px;
    margin-right: ${getSpacing("4x")};
  }
`;

const ImageContainer = styled.div<{ $withShadow: boolean }>`
  position: relative;
  background-color: ${Core.surface.primary};
  border-radius: 50%;
  width: ${IMAGE_DIMENSION}px;
  height: ${IMAGE_DIMENSION}px;
  overflow: hidden;
  ${(props) =>
    props.$withShadow
      ? css`
          border: 1px solid ${Core.border.subdued};
          box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.12);
        `
      : css`
          border: 1px solid ${Core.border.muted};
        `}

  img {
    display: block;
    width: 100%;
    height: 100%;
  }
`;
